import { alpha, Box, styled } from '@mui/material'
import { purple, red, teal, blue, indigo, lime } from '@mui/material/colors'

export const CodeAreaTheme = styled(Box, { shouldForwardProp: (prop: string) => !['size', 'height'].includes(prop) })<{
  size?: 'small' | 'medium'
  height?: number | string
}>(({ size, height, theme }) => ({
  position: 'relative',
  flex: 1,
  display: 'flex',
  cursor: 'text',

  ':focus-within &, .error &': {
    marginBottom: 0
  },

  '& .react-codemirror2': {
    width: '100%',
    height: '100% !important',
    position: 'relative'
  },
  '& .CodeMirror': {
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 1,
    height: '100% !important',
    backgroundColor: theme.palette.background.default
  },
  '& .CodeMirror-code': {
    '&> div:first-of-type .CodeMirror-line': {
      marginTop: theme.spacing(-0.25)
    }
  },
  '& .CodeMirror-scroll': {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    backgroundColor: 'transparent'
  },
  '& .CodeMirror-lines': {
    padding: theme.spacing(1, 0)
  },
  '& .CodeMirror pre.CodeMirror-line, & .CodeMirror pre.CodeMirror-line-like': {
    boxSizing: 'border-box',
    paddingLeft: 0,
    paddingTop: theme.spacing(0.25),
    lineHeight: 1.2
  },
  '& .CodeMirror-linenumbers': {
    width: `${theme.spacing(4.5)} !important`
  },
  '& .CodeMirror-linenumber': {
    width: `${theme.spacing(4.5)} !important`,
    height: theme.spacing(2),
    marginBottom: `${theme.spacing(-0.25)} !important`,
    paddingTop: theme.spacing(0.25),
    paddingRight: theme.spacing(1.25),
    boxSizing: 'border-box !important',
    color: theme.palette.text.secondary
  },

  // Active line addon
  '& .CodeMirror-activeline-background, & .CodeMirror-activeline-gutter': {
    backgroundColor: 'transparent'
  },
  '& .CodeMirror-gutter-background': {
    width: `${theme.spacing(6.25)} !important` // Hack needed to simulate active line background
  },
  '& .CodeMirror-sizer': {
    paddingTop: theme.spacing(0.25),
    marginLeft: `${theme.spacing(5.25)} !important`,
    borderRightWidth: `${theme.spacing(4)} !important` // Hack needed to simulate active line background
  },
  '& .CodeMirror-matchingbracket': {
    color: `${lime[900]} !important`,
    backgroundColor: theme.palette.black[4],
    boxShadow: `0 0 0 1px ${theme.palette.text.hint}`
  },

  // Selected line
  '& .CodeMirror-selected, & .CodeMirror-focused .CodeMirror-selected, & .CodeMirror-line::selection, & .CodeMirror-line > span::selection, & .CodeMirror-line > span > span::selection':
    {
      background: `${alpha(blue[800], 0.2)} !important`
    },

  '& .CodeMirror-gutters': {
    borderRight: 0,
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.default.background
  },
  '& .CodeMirror-gutter-elt': {
    '& .CodeMirror-foldgutter-open, .CodeMirror-foldgutter-folded': {
      marginLeft: theme.spacing(-1),

      '&:after': {
        fontSize: theme.typography.caption.fontSize
      }
    }
  },

  '& .CodeMirror-placeholder': {
    paddingTop: '0 !important',
    marginLeft: `${theme.spacing(-0.5)} !important`,
    marginBottom: `${theme.spacing(-0.25)} !important`,
    color: `${theme.palette.text.hint} !important`
  },
  '& .MuiLinearProgress-root': {
    height: '2px',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1
  },

  '& .pre.CodeMirror-line, .CodeMirror pre.CodeMirror-line-like, & .cm-bracket': {
    color: theme.palette.text.primary
  },
  '& .CodeMirror pre.CodeMirror-placeholder': {
    color: theme.palette.text.hint
  },
  '& .CodeMirror-composing': {
    border: 0
  },

  // Theme: light
  '& .cm-keyword, & .cm-string, & .cm-string-2, & .cm-other': {
    color: purple[500]
  },
  '& .cm-operator, & .cm-tag, & .cm-attribute': {
    color: teal[700]
  },
  '& .cm-number, & .cm-property, & .cm-builtin': {
    color: red[700]
  },
  '& .cm-comment': {
    color: theme.palette.text.secondary
  },
  '& .cm-variable': {
    color: lime[900]
  },
  '& .cm-variable-2': {
    color: theme.palette.text.primary
  },
  '& .cm-punctuation, & .cm-def, ': {
    color: blue[800]
  },
  '& .cm-variable-3, & .cm-type': {
    color: indigo[800]
  },
  '& .CodeMirror-foldmarker': {
    textShadow: 'none',
    color: theme.palette.primary.main
  },
  '& .cm-error': {
    color: theme.palette.error.main
  },
  '& .CodeMirror-selected': {
    background: blue[800]
  },

  ...(size === 'small' && {
    '& .CodeMirror': {
      height: height ? height : 'auto',
      minHeight: theme.spacing(9),
      ...theme.typography.code3
    }
  })
}))
